import { Dialog } from "@material-ui/core";
import { ReactNode, useRef, useState } from "react";
import { ACCOUNT_COPY } from "../../Services/API/UserAccountActions";
import { AccountOutcome } from "../../Types/Users";
import ConfirmationModal from "../Modal/Modal";
import Signifier from "../Signifier";

export interface ConfirmedAction {
  key: string;
  question?: string;
  run: () => Promise<AccountOutcome>;
  onSuccess?: () => void;
}

interface ConfirmedActionsProps {
  actions: ConfirmedAction[];
  children: (start: (key: string) => void, busy: boolean) => ReactNode;
}

export default function ConfirmedActions({ actions, children }: ConfirmedActionsProps) {
  const [confirmKey, setConfirmKey] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<AccountOutcome | null>(null);
  const [showSignifier, setShowSignifier] = useState(false);
  // Keying the Signifier by outcome remounts it, so each outcome restarts its slide-in animation.
  const [outcomeCount, setOutcomeCount] = useState(0);
  // A ref, so a second start in the same tick as the first still sees the run in flight.
  const busyRef = useRef(false);

  const execute = async (action: ConfirmedAction) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setBusy(true);

    let result: AccountOutcome;
    try {
      result = await action.run();
    } catch (err) {
      console.error(`${action.key} failed`, err);
      result = { ok: false, message: ACCOUNT_COPY.genericFailure };
    }

    busyRef.current = false;
    setOutcome(result);
    setOutcomeCount((count) => count + 1);
    setShowSignifier(true);
    setBusy(false);
    // Last: delete's onSuccess unmounts this component.
    if (result.ok) action.onSuccess?.();
  };

  const start = (key: string) => {
    const action = actions.find((a) => a.key === key);
    if (!action || busyRef.current) return;
    if (action.question) {
      setConfirmKey(key);
    } else {
      execute(action);
    }
  };

  const pending = actions.find((a) => a.key === confirmKey);

  return (
    <>
      {children(start, busy)}
      <Dialog open={confirmKey !== null} onClose={() => setConfirmKey(null)}>
        <ConfirmationModal
          question={pending?.question}
          func={() => pending && execute(pending)}
          closeModal={() => setConfirmKey(null)}
          processing={busy}
        />
      </Dialog>
      {outcome ? (
        <Signifier
          key={outcomeCount}
          showSignifier={showSignifier}
          setShowSignifier={setShowSignifier}
          message={outcome.message}
          error={!outcome.ok}
          errorMessage={outcome.message}
        />
      ) : null}
    </>
  );
}
